import { cell, pickField } from "@/utility/fclReportFieldUtils";
import {
    clipText,
    dateReportCol,
    EMPTY,
    pickDateSort,
    pickSort,
    reportCol,
} from "./importReportColumnUtils";

const codeCell = (value) =>
    clipText(value, "font-mono text-[12px] font-medium text-gray-800 dark:text-gray-200");

const statusBadge = (value, variant = "default") => {
    if (!value) return EMPTY;
    const styles = {
        danger: "bg-red-50 text-red-700 ring-red-200/80 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-800/60",
        success:
            "bg-emerald-50 text-emerald-700 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/60",
        default:
            "bg-gray-100 text-gray-700 ring-gray-200/80 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700/60",
    };
    return (
        <span
            className={`inline-flex max-w-full min-w-0 truncate rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ${styles[variant]}`}
            title={String(value)}
        >
            <span className="truncate">{value}</span>
        </span>
    );
};

export const IMPORT_BOOKING_LIST_COLUMNS = [
    reportCol({
        key: "mblNumber",
        label: "MBL NUMBER",
        width: 145,
        sticky: "left",
        render: (row) => codeCell(pickField(row, ["mblNumber", "MblNumber", "MBLNumber"])),
        sortValue: (row) => pickSort(row, ["mblNumber", "MblNumber", "MBLNumber"]),
    }),
    reportCol({
        key: "hblNumber",
        label: "HBL NUMBER",
        width: 155,
        sticky: "left",
        render: (row) => codeCell(pickField(row, ["hblNumber", "HblNumber", "HBLNumber"])),
        sortValue: (row) => pickSort(row, ["hblNumber", "HblNumber", "HBLNumber"]),
    }),
    reportCol({
        key: "containerNumber",
        label: "CNTR#",
        width: 165,
        render: (row) => clipText(pickField(row, ["containerNumber", "ContainerNumber"])),
        sortValue: (row) => pickSort(row, ["containerNumber", "ContainerNumber"]),
    }),
    reportCol({
        key: "size",
        label: "SIZE",
        width: 130,
        align: "center",
        render: (row) => clipText(pickField(row, ["size", "Size"]), "text-[12px] font-medium"),
        sortValue: (row) => pickSort(row, ["size", "Size"]),
    }),
    reportCol({
        key: "rateQuoteNo",
        label: "RATE QUOTE#",
        width: 115,
        align: "center",
        render: (row) => {
            const v = pickField(row, ["rateQuoteNo", "RateQuoteNo"]);
            if (!v) return <span className="text-[11px] font-semibold text-red-600 dark:text-red-400">-NA-</span>;
            return <span className="text-[12px] font-medium text-blue-600 dark:text-blue-400">{v}</span>;
        },
        sortValue: (row) => pickSort(row, ["rateQuoteNo", "RateQuoteNo"]),
    }),
    reportCol({
        key: "shipper",
        label: "SHIPPER",
        width: 220,
        render: (row) => clipText(pickField(row, ["shipper", "Shipper"])),
        sortValue: (row) => pickSort(row, ["shipper", "Shipper"]),
    }),
    reportCol({
        key: "billToParty",
        label: "BILL TO PARTY",
        width: 220,
        render: (row) => clipText(pickField(row, ["billToParty", "BillToParty"])),
        sortValue: (row) => pickSort(row, ["billToParty", "BillToParty"]),
    }),
    reportCol({
        key: "consignee",
        label: "CONSIGNEE",
        width: 220,
        render: (row) => clipText(pickField(row, ["consignee", "Consignee"])),
        sortValue: (row) => pickSort(row, ["consignee", "Consignee"]),
    }),
    reportCol({
        key: "referNo",
        label: "REFER#",
        width: 100,
        render: (row) => codeCell(pickField(row, ["referNo", "ReferNo", "referenceNo", "ReferenceNo"])),
        sortValue: (row) => pickSort(row, ["referNo", "ReferNo", "referenceNo", "ReferenceNo"]),
    }),
    reportCol({
        key: "origin",
        label: "ORIGIN",
        width: 82,
        align: "center",
        render: (row) => codeCell(pickField(row, ["origin", "Origin"])),
        sortValue: (row) => pickSort(row, ["origin", "Origin"]),
    }),
    reportCol({
        key: "destination",
        label: "DESTINATION",
        width: 95,
        align: "center",
        render: (row) => codeCell(pickField(row, ["destination", "Destination"])),
        sortValue: (row) => pickSort(row, ["destination", "Destination"]),
    }),
    dateReportCol({
        key: "etd",
        label: "ETD",
        width: 130,
        fieldKeys: ["etd", "ETD", "ets", "ETS"],
    }),
    dateReportCol({
        key: "eta",
        label: "ETA",
        width: 130,
        fieldKeys: ["eta", "ETA"],
    }),
    reportCol({
        key: "status",
        label: "STATUS",
        width: 125,
        render: (row) => {
            const v = pickField(row, ["status", "Status"]);
            if (!v) return EMPTY;
            const lower = String(v).toLowerCase();
            const variant =
                lower.includes("pending") || lower.includes("on the way") ? "danger" : "default";
            return statusBadge(v, variant);
        },
        sortValue: (row) => pickSort(row, ["status", "Status"]),
    }),
    dateReportCol({
        key: "ata",
        label: "ATA",
        width: 130,
        fieldKeys: ["ata", "ATA"],
    }),
    dateReportCol({
        key: "pU_Date",
        label: "P/U DATE",
        width: 130,
        fieldKeys: ["pU_Date", "PU_Date", "puDate", "PuDate"],
    }),
    reportCol({
        key: "confirmedDelivery",
        label: "CONFIRMED DELIVERY",
        width: 155,
        render: (row) => {
            const v = pickField(row, ["confirmedDelivery", "ConfirmedDelivery"]);
            if (!v) return EMPTY;
            const isPending = String(v).toLowerCase() === "pending";
            return statusBadge(v, isPending ? "danger" : "success");
        },
        sortValue: (row) => pickSort(row, ["confirmedDelivery", "ConfirmedDelivery"]),
    }),
    reportCol({
        key: "trucker",
        label: "TRUCKER",
        width: 140,
        render: (row) => clipText(pickField(row, ["trucker", "Trucker"])),
        sortValue: (row) => pickSort(row, ["trucker", "Trucker"]),
    }),
    reportCol({
        key: "comments",
        label: "COMMENTS",
        width: 160,
        render: (row) => clipText(pickField(row, ["comments", "Comments"])),
        sortValue: (row) => pickSort(row, ["comments", "Comments"]),
    }),
    reportCol({
        key: "complexity",
        label: "COMPLEXITY",
        width: 95,
        align: "center",
        render: (row) => {
            const v = pickField(row, ["complexity", "Complexity"], null);
            if (v == null || v === "") return EMPTY;
            return (
                <span className="tabular-nums font-semibold text-gray-800 dark:text-gray-200">
                    {String(v)}
                </span>
            );
        },
        sortValue: (row) => Number(pickField(row, ["complexity", "Complexity"], 0)) || 0,
    }),
];

export const IMPORT_BOOKING_COMPLEXITY_SUMMARY_COLUMNS = [
    reportCol({
        key: "customer",
        label: "CUSTOMER",
        width: 360,
        render: (row) =>
            clipText(
                pickField(row, ["customer", "Customer", "customerName", "CustomerName"])
            ),
        sortValue: (row) => pickSort(row, ["customer", "Customer", "customerName", "CustomerName"]),
    }),
    reportCol({
        key: "totalComplexity",
        label: "TOTAL COMPLEXITY",
        width: 160,
        align: "center",
        render: (row) => {
            const v = pickField(row, ["totalComplexity", "TotalComplexity", "complexity", "Complexity"], null);
            if (v == null || v === "") return EMPTY;
            return (
                <span className="tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
                    {String(v)}
                </span>
            );
        },
        sortValue: (row) =>
            Number(pickField(row, ["totalComplexity", "TotalComplexity", "complexity", "Complexity"], 0)) || 0,
    }),
];
